import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement } from "@stripe/react-stripe-js";

import logo from "../images/logo.png";
import TimerDisplay from "../components/TimerDisplay";
import { Icons } from "../components/Icons";
import { sendCode, approveCode, updateProfile } from "../services/api";
import {
	handlePhoneChange,
	formatPhoneforApi,
	handleEmailChange,
	validatePhone,
} from "./reservation/sign-up/StartSession.util";

// Hooks
import { useWalkUpFlow, WALKUP_STATES } from "../hooks/useWalkUpFlow";
import { useResolveUser } from "../hooks/useResolveUser";
import { useSetupCard } from "../hooks/useSetupCard";
import { useUnlockSession } from "../hooks/useUnlockSession";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Card Element styling
const cardElementOptions = {
	style: {
		base: {
			fontSize: "16px",
			fontFamily: '"Inter", sans-serif',
			color: "#424770",
			"::placeholder": {
				color: "#aab7c4",
			},
		},
	},
	hidePostalCode: true,
};

export default function WalkUpCheckin() {
	const navigate = useNavigate();
	const location = useLocation();
	const sessionData = location.state;

	// OTP state
	const [phone, setPhone] = useState("");
	const [code, setCode] = useState("");
	const [codeSentOut, setCodeSentOut] = useState(false);
	const [codeSentOutBtnDelay, setCodeSentOutBtnDelay] = useState(false);
	const [errorCode, setErrorCode] = useState(null);
	const [phoneError, setPhoneError] = useState(false);
	const [otpVerified, setOtpVerified] = useState(false);

	// Profile form state
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");

	// Error handling
	const [errorMessage, setErrorMessage] = useState("");
	const [hasResolvedUser, setHasResolvedUser] = useState(false);
	const [showBackConfirmation, setShowBackConfirmation] = useState(false);

	// Hooks
	const walkupFlow = useWalkUpFlow(sessionData?.boothObjectId);
	const resolveUser = useResolveUser();
	const unlockSession = useUnlockSession();

	// Handle user resolution after OTP verification
	useEffect(() => {
		const handleResolveUser = async () => {
			if (hasResolvedUser) return; // Prevent multiple calls
			if (walkupFlow.isLoading) return; // Don't trigger during other operations
			setHasResolvedUser(true);

			walkupFlow.resolveUserStart();

			try {
				const result = await resolveUser.resolve(
					formatPhoneforApi(phone),
					sessionData.boothObjectId
				);

				console.log("🔍 [DEBUG] API Response:", result);
				console.log("🔍 [DEBUG] SetupIntent in response:", result.setupIntent);

				walkupFlow.resolveUserSuccess(result);

				// Pre-fill form if user data exists
				if (result.user) {
					setFirstName(result.user.firstName || "");
					setLastName(result.user.lastName || "");
					setEmail(result.user.email || "");
				}
			} catch (error) {
				walkupFlow.resolveUserError(error.message);
				setErrorMessage(error.message);
			}
		};

		if (otpVerified && !hasResolvedUser && !walkupFlow.isLoading) {
			console.log("🔍 [DEBUG] OTP verified, calling handleResolveUser");
			handleResolveUser();
		}
	}, [
		otpVerified,
		hasResolvedUser,
		walkupFlow.isLoading,
		resolveUser,
		phone,
		sessionData.boothObjectId,
	]);

	// Handle automatic unlock for returning users with valid payment methods
	useEffect(() => {
		const handleReturnindUserUnlock = async () => {
			if (
				walkupFlow.currentState === WALKUP_STATES.PAYMENT_COMPLETE &&
				walkupFlow.user &&
				!walkupFlow.isLoading
			) {
				console.log(
					"🔍 [DEBUG] PAYMENT_COMPLETE state - processing unlock for returning user"
				);

				try {
					walkupFlow.unlockStart();

					// Process the unlock payment (creates session)
					const unlockResult = await unlockSession.unlock(
						walkupFlow.user.id,
						sessionData.boothObjectId
					);

					console.log(
						"🔍 [DEBUG] Session created for returning user:",
						unlockResult
					);

					// Navigate to session management page
					navigate(`/reservation/session/${unlockResult.session.id}`);
				} catch (error) {
					console.error(
						"❌ Failed to process unlock for returning user:",
						error
					);
					walkupFlow.unlockError(error.message);
				}
			}
		};

		handleReturnindUserUnlock();
	}, [
		walkupFlow.currentState,
		walkupFlow.user,
		walkupFlow.isLoading,
		sessionData.boothObjectId,
		navigate,
		unlockSession,
	]);

	// Check for session data
	if (!sessionData) {
		return (
			<div className="flex flex-col justify-center mb-8 md:px-mdPagePadding md:mx-smPagePadding lg:px-lgPagePadding lg:mx-lgPagePadding xl:px-xlPagePadding xl:mx-xlPagePadding">
				<div className="fixed top-0 left-0 right-0 m-8 text-black text-sm text-center rounded-secondary py-3 z-50 border border-red-300 bg-red-50 shadow-lg">
					<div className="flex flex-col items-center">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth={1.5}
							stroke="currentColor"
							className="size-6"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
							/>
						</svg>
						<span className="mt-2">
							No booth data found. Please scan the QR code again.
						</span>
					</div>
				</div>
			</div>
		);
	}

	// Event handlers
	const handlePhoneChangeWrapper = (element) =>
		handlePhoneChange(element, phone, setPhone, setPhoneError);

	const handleEmailChangeWrapper = (element) =>
		handleEmailChange(element, setEmail);

	const handleSendCode = async () => {
		try {
			setCodeSentOutBtnDelay(true);
			setTimeout(() => setCodeSentOutBtnDelay(false), 1000);

			const isPhoneValid = await validatePhone(phone);
			if (!isPhoneValid) {
				setPhoneError(true);
				return;
			}

			await sendCode(formatPhoneforApi(phone));
			setCodeSentOut(true);
		} catch (error) {
			if (error.message === "Incorrect phone number") {
				setPhoneError(true);
				return;
			}
			setErrorMessage("Error sending code");
		}
	};

	const handleCodeChange = (value) => {
		setErrorCode(null);
		if (value.length <= 4) {
			setCode(value);
			if (value.length === 4) {
				handleApproveCode(value);
			}
		}
	};

	const handleApproveCode = async (currentCode) => {
		try {
			await approveCode(formatPhoneforApi(phone), currentCode);
			setOtpVerified(true);
		} catch (error) {
			if (error.message === "Invalid code") {
				setErrorCode(error.message);
				return;
			}
			setErrorMessage("Error verifying code");
		}
	};

	const handleProfileSubmit = async () => {
		try {
			// Update profile on server
			await updateProfile(walkupFlow.user.id, firstName, lastName, email);

			walkupFlow.profileUpdated({
				firstName,
				lastName,
				email,
			});
		} catch (error) {
			setErrorMessage("Failed to update profile");
			console.error("Profile update error:", error);
		}
	};

	const handleGoBack = () => {
		// Validate sessionData exists
		if (!sessionData?.boothObjectId) {
			console.error("Cannot navigate back: missing booth data");
			return;
		}

		// Prevent navigation during loading states
		if (walkupFlow.isLoading) {
			console.warn("Cannot navigate back while operation in progress");
			return;
		}

		// Show confirmation if user has made significant progress
		const hasProgress =
			walkupFlow.currentState === WALKUP_STATES.NEEDS_CARD ||
			walkupFlow.currentState === WALKUP_STATES.SETTING_UP_CARD ||
			walkupFlow.currentState === WALKUP_STATES.PAYMENT_COMPLETE ||
			walkupFlow.currentState === WALKUP_STATES.UNLOCKING;

		if (hasProgress && !showBackConfirmation) {
			setShowBackConfirmation(true);
			return;
		}

		// Clean up session storage when explicitly going back
		walkupFlow.reset();

		// Navigate back with preserved booth data
		navigate(`/booth/${sessionData.boothObjectId}`, {
			state: {
				boothObjectId: sessionData.boothObjectId,
				boothId: sessionData.boothId,
				boothData: sessionData.boothData,
			},
		});
	};

	const confirmGoBack = () => {
		setShowBackConfirmation(false);
		// Clean up and navigate
		walkupFlow.reset();
		navigate(`/booth/${sessionData.boothObjectId}`, {
			state: {
				boothObjectId: sessionData.boothObjectId,
				boothId: sessionData.boothId,
				boothData: sessionData.boothData,
			},
		});
	};

	const cancelGoBack = () => {
		setShowBackConfirmation(false);
	};

	// Render functions
	const renderOTPSection = () => (
		<>
			<div>
				<label className="block text-sm text-textSecondary">Phone Number</label>
				<div className="relative">
					<input
						type="text"
						placeholder="+1 (234) 567-8910"
						required
						className={`mt-2 block w-full py-3 px-4 border ${
							phoneError ? "border-red-500" : "border-gray-300"
						} rounded-secondary shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50`}
						value={phone}
						onChange={handlePhoneChangeWrapper}
					/>
					<button
						className={`absolute right-0 px-4 mr-1 flex items-center text-xs font-medium leading-4 text-white rounded-secondary ${
							phone.length < 1 || codeSentOutBtnDelay
								? "bg-gray-500"
								: "bg-gray-900"
						}`}
						disabled={phone.length < 1 || codeSentOutBtnDelay}
						style={{
							top: "50%",
							transform: "translateY(-50%)",
							height: "80%",
						}}
						onClick={handleSendCode}
					>
						{codeSentOutBtnDelay ? Icons.phoneSpinner : "→"}
					</button>
				</div>
				{phoneError && (
					<div className="text-red-500 text-sm mt-1">
						<span>Not a valid phone number.</span>
					</div>
				)}
			</div>
			<div className="mt-4">
				<label className="block text-sm text-textSecondary">
					4 Digit Code (Sent via text)
				</label>
				<div className="relative">
					<input
						type="number"
						placeholder="1234"
						required
						className={`mt-2 block w-full py-3 px-4 border border-gray-300 rounded-secondary shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50
						${codeSentOut ? "bg-white" : "bg-gray-200"}
						${errorCode ? "border-red-500" : "border-gray-300"}
						`}
						disabled={!codeSentOut}
						value={code}
						onChange={(e) => {
							handleCodeChange(e.target.value);
						}}
					/>
					{codeSentOut && (
						<div className="absolute right-0 top-0 h-full flex items-center pr-4">
							<div className="text-sm bg-green-300 px-3 py-1 rounded-md text-gray-900">
								Code Sent!
							</div>
						</div>
					)}
				</div>
				{errorCode && (
					<div className="text-red-500 text-sm mt-1">
						<span>{errorCode}</span>
					</div>
				)}
			</div>
		</>
	);

	const renderProfileSection = () => (
		<div className="flex flex-col mt-1">
			<div>
				<label className="block text-sm text-textSecondary">Phone number</label>
				<div className="flex justify-between items-center mt-2 w-full py-3 px-4 border border-gray-300 bg-gray-50 text-gray-500 rounded-secondary shadow-sm text-sm">
					{phone}
					<span className="bg-green-300 text-xs px-3 py-1 rounded-md text-gray-700">
						Verified ✓
					</span>
				</div>
			</div>

			<div className="mt-8 mb-6">
				<h2 className="text-2xl font-bold text-black tracking-[-0.24px]">
					Complete Your Profile
				</h2>
			</div>

			<div className="flex flex-col gap-6">
				<div>
					<label className="block text-sm text-textSecondary">First Name</label>
					<input
						type="text"
						placeholder="John"
						required
						className={`mt-2 block w-full py-3 px-4 border rounded-secondary shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50`}
						value={firstName}
						onChange={(e) => setFirstName(e.target.value)}
					/>
				</div>

				<div>
					<label className="block text-sm text-textSecondary">Last Name</label>
					<input
						type="text"
						placeholder="Smith"
						required
						className={`mt-2 block w-full py-3 px-4 border rounded-secondary shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50`}
						value={lastName}
						onChange={(e) => setLastName(e.target.value)}
					/>
				</div>

				<div>
					<label className="block text-sm text-textSecondary">Email</label>
					<input
						type="email"
						placeholder="john.smith@gmail.com"
						required
						className={`mt-2 block w-full py-3 px-4 border rounded-secondary shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50`}
						value={email}
						onChange={handleEmailChangeWrapper}
					/>
				</div>
			</div>
		</div>
	);

	const renderCardSection = () => (
		<div className="flex flex-col mt-1">
			<div>
				<label className="block text-sm text-textSecondary">Phone number</label>
				<div className="flex justify-between items-center mt-2 w-full py-3 px-4 border border-gray-300 bg-gray-50 text-gray-500 rounded-secondary shadow-sm text-sm">
					{phone}
					<span className="bg-green-300 text-xs px-3 py-1 rounded-md text-gray-700">
						Verified ✓
					</span>
				</div>
			</div>

			<div className="mt-8 mb-6">
				<h2 className="text-2xl font-bold text-black tracking-[-0.24px]">
					Add Payment Method
				</h2>
				<p className="text-textSecondary mt-2">
					We'll charge $1 to unlock the booth, then bill you per minute when you
					leave.
				</p>
			</div>

			<div className="flex flex-col gap-6">
				<div>
					<label className="block text-base font-semibold text-black mb-2 font-['Inter']">
						Card Details
					</label>
					<div className="block w-full py-4 px-4 border border-gray-300 bg-white rounded-[10px] shadow-sm focus-within:border-blue-500 focus-within:ring focus-within:ring-blue-200 focus-within:ring-opacity-50">
						<CardElement options={cardElementOptions} />
					</div>
				</div>
			</div>
		</div>
	);

	// Component for Stripe Elements provider
	const CardSetupForm = () => {
		const setupCard = useSetupCard();

		// Don't render if we don't have the required data
		if (!walkupFlow.setupIntent || !walkupFlow.user) {
			return null;
		}

		const handleCardSetup = async () => {
			try {
				// Don't change state until we start the actual Stripe operation
				// This prevents the CardElement from unmounting

				// Setup the card and attach payment method to customer
				// Extract clientSecret if setupIntent is an object
				const clientSecret = walkupFlow.setupIntent.clientSecret || walkupFlow.setupIntent;
				await setupCard.setupCard(
					walkupFlow.user.id,
					clientSecret
				);

				// Only change state after successful card setup
				walkupFlow.cardSetupSuccess();

				// Wait a moment for the payment method to be fully processed
				console.log("🔍 [DEBUG] Payment method attached, processing unlock...");
				await new Promise((resolve) => setTimeout(resolve, 1000));

				// Process the unlock payment with retry logic for timing issues
				let unlockResult;
				let retryCount = 0;
				const maxRetries = 3;

				while (retryCount < maxRetries) {
					try {
						unlockResult = await unlockSession.unlock(
							walkupFlow.user.id,
							sessionData.boothObjectId
						);
						break; // Success, exit retry loop
					} catch (error) {
						retryCount++;
						if (
							error.message.includes("No valid payment method found") &&
							retryCount < maxRetries
						) {
							console.log(
								`🔍 [DEBUG] Payment method not found, retrying in 2 seconds... (attempt ${retryCount}/${maxRetries})`
							);
							setErrorMessage(
								`Setting up payment method... (${retryCount}/${maxRetries})`
							);
							await new Promise((resolve) => setTimeout(resolve, 2000));
						} else {
							throw error; // Re-throw if not a payment method issue or max retries reached
						}
					}
				}

				// Clear any error messages from retry attempts
				setErrorMessage("");

				// Now update state and navigate (after successful completion)
				walkupFlow.paymentComplete(unlockResult.session);

				// Navigate to the reservation page for manual unlock
				navigate(`/reservation/session/${unlockResult.session.id}`);
			} catch (error) {
				console.error("🔍 [DEBUG] Card setup error:", error);
				// Only change state to error if something fails
				walkupFlow.cardSetupError(error.message);
				setErrorMessage(error.message);
			}
		};

		return (
			<>
				{renderCardSection()}
				<div className="mt-8">
					<button
						onClick={handleCardSetup}
						disabled={
							setupCard.processing ||
							!setupCard.isStripeLoaded ||
							walkupFlow.isLoading
						}
						className={`w-full py-4 rounded-secondary font-semibold text-white transition-colors ${
							setupCard.processing ||
							!setupCard.isStripeLoaded ||
							walkupFlow.isLoading
								? "bg-gray-400"
								: "bg-black hover:bg-gray-700"
						}`}
					>
						{setupCard.processing || walkupFlow.isLoading
							? "Setting up..."
							: "Add Payment Method"}
					</button>
				</div>
			</>
		);
	};

	// Get current action button
	const getCurrentActionButton = () => {
		const baseButtonClasses =
			"w-full py-3 ml-7 rounded-secondary font-semibold transition-colors pointer-events-auto font-inter";

		switch (walkupFlow.currentState) {
			case WALKUP_STATES.OTP_VERIFIED:
				return null;

			case WALKUP_STATES.NEEDS_PROFILE:
				const profileValid = firstName && lastName && email;
				return (
					<button
						onClick={handleProfileSubmit}
						disabled={!profileValid}
						className={`${baseButtonClasses} ${
							!profileValid ? "bg-gray-400" : "bg-black hover:bg-gray-700"
						} text-white`}
					>
						Continue
					</button>
				);

			case WALKUP_STATES.NEEDS_CARD:
				return null; // Handled by CardSetupForm

			case WALKUP_STATES.PAYMENT_COMPLETE:
				return (
					<div className="text-center py-4">
						<div className="text-lg font-semibold mb-2">Payment Complete!</div>
						<div className="text-textSecondary">
							Redirecting to unlock page...
						</div>
					</div>
				);

			default:
				if (!otpVerified) {
					const otpValid = codeSentOut && code.length === 4;
					return (
						<button
							disabled={!otpValid}
							className={`${baseButtonClasses} ${
								!otpValid ? "bg-gray-400" : "bg-black hover:bg-gray-700"
							} text-white`}
						>
							Continue
						</button>
					);
				}
				return null;
		}
	};

	return (
		<section className="flex flex-col justify-center mb-8 md:px-mdPagePadding md:mx-smPagePadding lg:px-lgPagePadding lg:mx-lgPagePadding xl:px-xlPagePadding xl:mx-xlPagePadding">
			{/* Back confirmation modal */}
			{showBackConfirmation && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
					<div className="bg-white rounded-lg p-6 max-w-sm w-full shadow-xl">
						<h3 className="text-xl font-bold text-black mb-3">
							Leave this page?
						</h3>
						<p className="text-textSecondary mb-6">
							Your progress will be lost. Are you sure you want to go back?
						</p>
						<div className="flex gap-3">
							<button
								onClick={cancelGoBack}
								className="flex-1 py-3 px-4 border border-gray-300 rounded-secondary font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
							>
								Stay
							</button>
							<button
								onClick={confirmGoBack}
								className="flex-1 py-3 px-4 bg-black text-white rounded-secondary font-semibold hover:bg-gray-800 transition-colors"
							>
								Leave
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Loading overlay */}
			{walkupFlow.isLoading && (
				<div className="fixed inset-0 bg-white/80 flex items-center justify-center z-50">
					<div className="flex flex-col items-center">
						<div className="w-10 h-10 border-t-2 border-b-2 border-gray-900 rounded-full animate-spin"></div>
						<p className="mt-4 text-gray-900">
							{walkupFlow.currentState === WALKUP_STATES.RESOLVING_USER &&
								"Resolving user..."}
							{walkupFlow.currentState === WALKUP_STATES.SETTING_UP_CARD &&
								"Setting up payment..."}
							{walkupFlow.currentState === WALKUP_STATES.UNLOCKING &&
								"Unlocking booth..."}
						</p>
					</div>
				</div>
			)}

			{/* Error message */}
			{errorMessage && (
				<div className="fixed top-0 left-0 right-0 m-8 text-black text-sm text-center rounded-secondary py-3 z-50 border border-red-300 bg-red-50 shadow-lg animate-slideInDown">
					<div className="flex flex-col items-center">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth={1.5}
							stroke="currentColor"
							className="size-6"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
							/>
						</svg>
						<span className="mt-2">{errorMessage}</span>
					</div>
				</div>
			)}

			{/* Header */}
			<div className="flex flex-col justify-center items-center px-2 py-8">
				<img src={logo} alt="logo" className="w-40 h-auto object-contain" />
				<h1 className="text-2xl font-bold mt-4 text-center">
					Confirm it's you
				</h1>
			</div>

			{/* Booth Details */}
			<div className="border-t border-b border-gray-200 py-5 mb-4 mx-smPagePadding flex flex-col justify-center items-center">
				<div className="flex items-center gap-3 text-textSecondary">
					Pod {sessionData.boothId} | {sessionData.boothData?.location?.name}
				</div>
			</div>

			{/* Timer */}
			<TimerDisplay
				initialTime={300}
				onExpire={() => setErrorMessage("Reservation expired")}
			/>

			{/* Form */}
			<div className="mx-smPagePadding">
				<form className="flex flex-col mt-2 md:px-mdPagePadding lg:px-20">
					{/* OTP Section - Show until verified */}
					{!otpVerified && renderOTPSection()}

					{/* Profile Section - Show when needs profile */}
					{walkupFlow.needsProfile && renderProfileSection()}

					{walkupFlow.needsCard && walkupFlow.setupIntent && (
						<Elements
							stripe={stripePromise}
							key={`walkup-card-elements-${walkupFlow.setupIntent.id}`}
						>
							<CardSetupForm
								key={`walkup-card-form-${walkupFlow.setupIntent.id}`}
							/>
						</Elements>
					)}
					{/* Temporary debug section */}
					{walkupFlow.needsCard && !walkupFlow.setupIntent && (
						<div className="bg-yellow-100 border border-yellow-400 p-4 rounded">
							<p className="text-yellow-800">
								DEBUG: Needs card but no setupIntent
							</p>
							<pre className="text-xs mt-2">
								{JSON.stringify(walkupFlow, null, 2)}
							</pre>
						</div>
					)}

					{/* Ready to unlock */}
					{walkupFlow.currentState === WALKUP_STATES.UNLOCKING && (
						<div className="text-center py-8">
							<div className="text-lg font-semibold mb-4">Ready to unlock!</div>
							<div className="text-textSecondary">
								Booth rate: ${walkupFlow.booth?.ratePerMinute || 0}/minute
							</div>
						</div>
					)}
				</form>

				{/* Action Buttons */}
				<div className="left-0 right-0 flex justify-center items-center z-50 pointer-events-none mt-5 md:mt-10 md:mx-12 lg:mt-12 lg:mx-20">
					<button
						className="bg-white px-4 py-3 rounded-secondary border border-gray-400 pointer-events-auto"
						onClick={handleGoBack}
					>
						<i className="fas fa-arrow-left fa-lg"></i>
					</button>
					{getCurrentActionButton()}
				</div>

				{/* Support Contact */}
				<div className="flex flex-col items-center mt-16 text-center text-sm text-gray-600">
					<p>Need help?</p>
					<p>
						Contact support at{" "}
						<span className="font-semibold underline">
							team@pebblespaces.com
						</span>
					</p>
				</div>
			</div>
		</section>
	);
}
